import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

/** Espelha `ServiceResponseDto`. */
export interface Service {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  description?: string;
  unit: string;
  price?: number;
  nbsCode?: string;
  lc116Code?: string;
  issAliquota?: number;
  issDeducao: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceInput {
  name: string;
  code: string;
  description?: string;
  unit?: string;
  price?: number;
}

export interface Category {
  id: string;
  name: string;
  description: string;
  active: boolean;
  createdAt: string;
}

/**
 * Espelha `CustomerResponseDto`.
 *
 * Nao existe `active`: existe `isBlocked`, que e o **inverso**. A tela lia
 * `active` (sempre `undefined`, logo falso) e marcava todo cliente como
 * inativo.
 */
export interface Customer {
  id: string;
  partyId: string;
  companyId: string;
  name: string;
  legalName?: string;
  documentType?: string;
  documentNumber?: string;
  email?: string;
  phone?: string;
  creditLimit: number;
  isBlocked: boolean;
  externalCode?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerAddress {
  street?: string;
  number?: string;
  complement?: string;
  district?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface CustomerDetail extends Customer {
  tenantId?: string;
  salesPersonId?: string;
  paymentTerms?: string;
  priceTable?: string;
  stateRegistration?: string;
  municipalRegistration?: string;
  mobile?: string;
  website?: string;
  notes?: string;
  tags?: string[];
  blockReason?: string;
  address?: CustomerAddress;
}

export interface CustomerInput {
  name: string;
  legalName?: string;
  documentType: string;
  documentNumber: string;
  email?: string;
  phone?: string;
  creditLimit?: number;
}

export type ServicePayload = ServiceInput;
export type CategoryPayload = Omit<Category, 'id' | 'createdAt'>;
export type CustomerPayload = CustomerInput;

/* ── Hooks ── */

// Services
export function useServices() {
  return useQuery({
    queryKey: queryKeys.masterData.services(),
    queryFn: () => apiClient.get<Service[]>('/services'),
  });
}

export function useCreateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ServicePayload) =>
      apiClient.post<Service>('/services', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.services() });
    },
  });
}

export function useUpdateService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: ServicePayload & { id: string }) =>
      apiClient.patch<Service>(`/services/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.services() });
    },
  });
}

export function useDeleteService() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/services/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.services() });
    },
  });
}

// Categories
export function useCategories() {
  return useQuery({
    queryKey: queryKeys.masterData.categories(),
    queryFn: () => apiClient.get<Category[]>('/categories'),
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CategoryPayload) =>
      apiClient.post<Category>('/categories', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.categories() });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: CategoryPayload & { id: string }) =>
      apiClient.patch<Category>(`/categories/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.categories() });
    },
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.categories() });
    },
  });
}

// Customers
export function useCustomers() {
  return useQuery({
    queryKey: queryKeys.masterData.customers(),
    queryFn: () => apiClient.get<Customer[]>('/customers'),
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.masterData.customers(), id],
    queryFn: () => apiClient.get<CustomerDetail>(`/customers/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CustomerPayload) =>
      apiClient.post<Customer>('/customers', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.customers() });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: CustomerPayload & { id: string }) =>
      apiClient.patch<Customer>(`/customers/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.customers() });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/customers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.masterData.customers() });
    },
  });
}
